import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDismissOnOutside } from "@/lib/dismissOnOutside";

/**
 * The app's one right-click menu.
 *
 * Anatomy is `MenuSelect`'s — same row height, same type, same opaque surface —
 * because a context menu that looks like a different component makes the app
 * feel like two apps. What differs is only where it opens: at the pointer,
 * flipped back inside the viewport when the pointer was near an edge.
 */

export type ContextMenuAction = {
  id: string;
  label: string;
  /** Rendered right-aligned and quiet, e.g. "⌘⌥C". Omit where the platform
   * already owns the key (⌘W is the browser's, not ours). */
  shortcut?: string;
  disabled?: boolean;
  danger?: boolean;
  onSelect: () => void;
};

export type ContextMenuEntry = ContextMenuAction | { separator: true };

export type ContextMenuState = {
  x: number;
  y: number;
  items: ContextMenuEntry[];
  /** What the menu hangs off: the button that opened it, or the row that was
   * right-clicked. Used to decide whether a scroll actually moved the menu's
   * anchor, to keep pressing the trigger a toggle rather than a reopen, and to
   * hand focus back when the menu closes. */
  anchor?: HTMLElement | null;
  /** "end" hangs the menu off its trailing edge at `x`, for a trigger at the
   * right of the window. */
  align?: "start" | "end";
};

function isAction(entry: ContextMenuEntry): entry is ContextMenuAction {
  return !("separator" in entry);
}

/** Opens the menu from any `onContextMenu` handler and holds its position. */
export function useContextMenu() {
  const [state, setState] = useState<ContextMenuState | null>(null);
  const open = useCallback((
    event: { clientX: number; clientY: number; preventDefault: () => void; currentTarget?: unknown },
    items: ContextMenuEntry[],
    anchor?: HTMLElement | null,
  ) => {
    event.preventDefault();
    if (items.length === 0) return;
    const from = anchor ?? (event.currentTarget instanceof HTMLElement ? event.currentTarget : null);
    setState({ x: event.clientX, y: event.clientY, items, anchor: from });
  }, []);
  const close = useCallback(() => setState(null), []);
  /** For a trigger button: pressing it again closes rather than reopens. */
  const toggle = useCallback((
    event: { clientX: number; clientY: number; preventDefault: () => void; currentTarget?: unknown },
    items: ContextMenuEntry[],
    anchor?: HTMLElement | null,
    align?: ContextMenuState["align"],
  ) => {
    event.preventDefault();
    setState((current) => {
      if (current) return null;
      if (items.length === 0) return null;
      const from = anchor ?? (event.currentTarget instanceof HTMLElement ? event.currentTarget : null);
      return { x: event.clientX, y: event.clientY, items, anchor: from, align };
    });
  }, []);
  return { state, open, toggle, close };
}

export function ContextMenu({ state, onClose }: { state: ContextMenuState | null; onClose: () => void }) {
  const menuRef = useRef<HTMLDivElement>(null);
  // Mirrors `state.anchor` so the dismiss hook can read it without the array of
  // refs changing identity every render.
  const anchorRef = useRef<HTMLElement | null>(null);
  anchorRef.current = state?.anchor ?? null;
  const dismissRefs = useMemo(() => [menuRef, anchorRef], []);
  const dismissOptions = useMemo(() => ({ anchor: anchorRef }), []);
  /** The trigger to hand focus back to once the menu goes away. */
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  /** The measurement, tagged with the open state it was taken for — so a
   * placement can never outlive the menu it belongs to, and the render can tell
   * "not measured yet" from "measured for a previous open" without a reset. */
  const [placement, setPlacement] = useState<{ state: ContextMenuState; left: number; top: number } | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const actionIndexes = (state?.items ?? []).flatMap((entry, index) => isAction(entry) && !entry.disabled ? [index] : []);

  // Reset *and* measure in one layout effect. They used to be two — a passive
  // effect that cleared the position and a layout effect that set it — and the
  // clear always won: React flushes pending passive effects before it renders
  // the update a layout effect scheduled, so the freshly measured position was
  // overwritten by `null` and the menu stayed `visibility: hidden` forever, in
  // silence. That is FRNT-693's "the + button does nothing", and it applied to
  // every right-click menu in the app as well.
  //
  // Measuring here (rather than in an effect that runs after paint) is also
  // what puts a menu opened near the right or bottom edge back inside the
  // viewport before its first paint, flipped rather than clipped.
  useLayoutEffect(() => {
    const element = menuRef.current;
    if (!state || !element) {
      setPlacement(null);
      return;
    }
    setActiveIndex(state.items.findIndex((entry) => isAction(entry) && !entry.disabled));
    const { width, height } = element.getBoundingClientRect();
    const margin = 8;
    const left = state.align === "end" ? Math.max(margin, state.x - width) : state.x + width + margin > window.innerWidth ? Math.max(margin, state.x - width) : state.x;
    const top = state.y + height + margin > window.innerHeight ? Math.max(margin, state.y - height) : state.y;
    setPlacement({ state, left, top });
  }, [state]);

  const placed = placement && placement.state === state ? placement : null;

  // Focus only once the menu is actually visible. `HTMLElement.focus()` on a
  // `visibility: hidden` element is a no-op, so focusing in the effect above —
  // which runs on the commit that still has the menu hidden — left the keyboard
  // with nothing: Escape, the arrows and Enter were all dead.
  useLayoutEffect(() => {
    if (!placed) return;
    menuRef.current?.focus();
  }, [placed]);

  // Opened from a button, closed by Escape or by picking an item: focus belongs
  // back on the button. Only when the menu still holds it — if the user clicked
  // somewhere else, that click owns focus now and this must not steal it.
  useLayoutEffect(() => {
    if (state) {
      restoreFocusRef.current = state.anchor instanceof HTMLButtonElement ? state.anchor : null;
      return;
    }
    const trigger = restoreFocusRef.current;
    restoreFocusRef.current = null;
    if (trigger?.isConnected && document.activeElement === document.body) trigger.focus();
  }, [state]);

  // Press outside, scroll, resize, or leave the window and the menu is gone —
  // the same four gestures the account menu dismisses on.
  useDismissOnOutside(Boolean(state), dismissRefs, onClose, dismissOptions);

  if (!state) return null;

  const move = (delta: number) => {
    if (actionIndexes.length === 0) return;
    const current = actionIndexes.indexOf(activeIndex);
    const next = actionIndexes[(current + delta + actionIndexes.length) % actionIndexes.length];
    if (next !== undefined) setActiveIndex(next);
  };

  const run = (item: ContextMenuAction) => {
    if (item.disabled) return;
    onClose();
    item.onSelect();
  };

  return createPortal(
    <div
      ref={menuRef}
      className="context-menu"
      role="menu"
      tabIndex={-1}
      style={{ left: placed?.left ?? state.x, top: placed?.top ?? state.y, visibility: placed ? "visible" : "hidden" }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          onClose();
        } else if (event.key === "ArrowDown") {
          event.preventDefault();
          move(1);
        } else if (event.key === "ArrowUp") {
          event.preventDefault();
          move(-1);
        } else if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          const item = state.items[activeIndex];
          if (item && isAction(item)) run(item);
        }
      }}
      onContextMenu={(event) => event.preventDefault()}
    >
      {state.items.map((entry, index) => isAction(entry) ? (
        <button
          key={entry.id}
          type="button"
          role="menuitem"
          className={`context-menu-item${entry.danger ? " danger" : ""}`}
          data-active={index === activeIndex}
          disabled={entry.disabled}
          onMouseEnter={() => setActiveIndex(index)}
          onClick={() => run(entry)}
        >
          <span className="context-menu-label">{entry.label}</span>
          {entry.shortcut ? <span className="context-menu-shortcut">{entry.shortcut}</span> : null}
        </button>
      ) : (
        <span key={`separator-${index}`} className="context-menu-separator" role="separator" />
      ))}
    </div>,
    document.body,
  );
}
